import { AlertTriangle, Plus, Edit, Trash2 } from 'lucide-react';
import { useI18n } from '@/contexts/i18n-hook';
import { traduzirClassificacao } from './constants/classificacao-risco';
import { rotuloListaDominio, rotuloTipoPerigo } from './constants/rotulos-dominio';

interface Risco {
  id?: string;
  numero: number;
  parte: string;
  hrnAtual: number;
  classificacaoRisco?: string;
  plr?: string;
  sil?: string;
  categoriaSeguranca?: string;
  tipoPerigo?: string;
  categoria?: string;
  conformeAtual?: boolean;
}

interface TabelaRiscosProps {
  riscos: Risco[];
  onAdicionar?: () => void;
  onEditar?: (risco: Risco) => void;
  onRemover?: (riscoId: string) => void;
  modoEdicao?: boolean;
}

export function TabelaRiscos({
  riscos,
  onAdicionar,
  onEditar,
  onRemover,
  modoEdicao = false,
}: TabelaRiscosProps) {
  const { t } = useI18n();

  const getClassificacaoColor = (classificacao?: string) => {
    const classLower = classificacao?.toLowerCase();
    switch (classLower) {
      case 'crítico':
      case 'critico':
        return 'bg-red-100 text-red-800';
      case 'alto':
        return 'bg-orange-100 text-orange-800';
      case 'médio':
      case 'medio':
        return 'bg-yellow-100 text-yellow-800';
      case 'baixo':
        return 'bg-green-100 text-green-800';
      case 'baixo, porém significativo':
        return 'bg-yellow-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Escala do cliente (Arcelor Mittal) — mesma de TabelaPartes/HrnTablesSection.
  const getHRNClassificacao = (hrn: number): string => {
    if (hrn >= 1000) return 'Inaceitável';
    if (hrn >= 500) return 'Extremo';
    if (hrn >= 100) return 'Muito alto';
    if (hrn >= 50) return 'Alto';
    if (hrn >= 10) return 'Significante';
    if (hrn >= 5) return 'Baixo';
    if (hrn >= 1) return 'Muito baixo';
    return 'Aceitável';
  };

  const getHRNColor = (hrn: number) => {
    const classificacao = getHRNClassificacao(hrn);
    return getClassificacaoColor(classificacao);
  };

  return (
    <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <AlertTriangle className="h-6 w-6 text-blue-500 dark:text-schmersal-blue mr-2" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('apreciacaoTabelas.riscos.titulo')}
          </h2>
        </div>
        {modoEdicao && onAdicionar && (
          <button
            type="button"
            onClick={onAdicionar}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 dark:bg-schmersal-blue hover:bg-blue-700 dark:hover:bg-schmersal-blue-light"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('apreciacaoTabelas.riscos.acao.adicionar')}
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-schmersal-gray-dark">
          <thead className="bg-gray-50 dark:bg-schmersal-gray-dark">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                #
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                {t('apreciacaoTabelas.riscos.coluna.descricao')}
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                HRN
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                {t('apreciacaoTabelas.riscos.coluna.classificacao')}
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                PLr
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                SIL
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                Cat
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                {t('apreciacaoTabelas.riscos.coluna.conformidade')}
              </th>
              {modoEdicao && (
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-white uppercase tracking-wider">
                  {t('comum.rotulo.acoes')}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-schmersal-gray-darker divide-y divide-gray-200 dark:divide-schmersal-gray-dark">
            {riscos.length === 0 ? (
              <tr>
                <td
                  colSpan={modoEdicao ? 9 : 8}
                  className="px-4 py-8 text-center text-sm text-gray-500 dark:text-white"
                >
                  {t('apreciacaoTabelas.riscos.vazio')}
                </td>
              </tr>
            ) : (
              riscos.map((risco) => {
                const classificacao =
                  risco.classificacaoRisco ||
                  getHRNClassificacao(risco.hrnAtual);
                return (
                  <tr key={risco.id || risco.numero} className="hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {risco.numero}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      <div>
                        <div className="font-medium">{risco.parte}</div>
                        {risco.tipoPerigo && (
                          <div className="text-xs text-gray-500 dark:text-schmersal-gray-light">
                            {rotuloListaDominio(t, risco.tipoPerigo, rotuloTipoPerigo)} - {risco.categoria}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getHRNColor(
                          risco.hrnAtual
                        )}`}
                      >
                        {risco.hrnAtual}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getClassificacaoColor(
                          classificacao
                        )}`}
                      >
                        {traduzirClassificacao(t, classificacao)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                      {risco.plr || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                      {risco.sil || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                      {risco.categoriaSeguranca || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          risco.conformeAtual
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {risco.conformeAtual
                          ? t('apreciacaoTabelas.riscos.conformidade.conforme')
                          : t('apreciacaoTabelas.riscos.conformidade.naoConforme')}
                      </span>
                    </td>
                    {modoEdicao && (
                      <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium">
                        <div className="flex justify-center space-x-2">
                          {onEditar && (
                            <button
                              type="button"
                              onClick={() => onEditar(risco)}
                              className="text-blue-600 dark:text-white hover:text-blue-900 dark:hover:text-schmersal-yellow"
                              title={t('comum.acao.editar')}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {onRemover && (
                            <button
                              type="button"
                              onClick={() => onRemover(risco.id || risco.numero.toString())}
                              className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                              title={t('comum.acao.remover')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Resumo */}
      {riscos.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 text-sm">
            <div>
              <span className="text-gray-500">{t('apreciacaoTabelas.riscos.resumo.totalRiscos')}</span>
              <span className="ml-2 font-semibold text-gray-900">
                {riscos.length}
              </span>
            </div>
            <div>
              <span className="text-gray-500">{t('apreciacaoTabelas.riscos.resumo.criticos')}</span>
              <span className="ml-2 font-semibold text-red-600">
                {
                  riscos.filter(
                    (r) =>
                      getHRNClassificacao(r.hrnAtual) === 'Crítico' ||
                      r.classificacaoRisco?.toLowerCase() === 'crítico'
                  ).length
                }
              </span>
            </div>
            <div>
              <span className="text-gray-500">{t('apreciacaoTabelas.riscos.resumo.altos')}</span>
              <span className="ml-2 font-semibold text-orange-600">
                {
                  riscos.filter(
                    (r) =>
                      getHRNClassificacao(r.hrnAtual) === 'Alto' ||
                      r.classificacaoRisco?.toLowerCase() === 'alto'
                  ).length
                }
              </span>
            </div>
            <div>
              <span className="text-gray-500">{t('apreciacaoTabelas.riscos.resumo.mediosBaixos')}</span>
              <span className="ml-2 font-semibold text-green-600">
                {
                  riscos.filter(
                    (r) =>
                      ['Médio', 'Baixo'].includes(
                        getHRNClassificacao(r.hrnAtual)
                      ) ||
                      ['médio', 'medio', 'baixo'].includes(
                        r.classificacaoRisco?.toLowerCase() || ''
                      )
                  ).length
                }
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

